import axios from "axios";

const axiosInstance = axios.create({
  baseURL: "/api", // Set your base URL
});

// Single-flight refresh implementation
let isRefreshing = false;
let refreshSubscribers = [];

// Retry configuration for connection errors
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

/**
 * Check if an error is a connection error that should be retried
 */
function isConnectionError(error) {
  // No response means the server didn't respond (connection refused, network error, timeout)
  if (!error.response) {
    // Check for specific connection error codes
    const code = error.code;
    if (
      code === "ECONNREFUSED" ||
      code === "ENOTFOUND" ||
      code === "ETIMEDOUT" ||
      code === "ERR_NETWORK" ||
      code === "ERR_INTERNET_DISCONNECTED" ||
      error.message?.includes("Network Error") ||
      error.message?.includes("timeout")
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Calculate delay for exponential backoff retry
 */
function getRetryDelay(retryCount) {
  return INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Add a callback to be invoked once a new token is available.
 * Callback receives the new access token as argument.
 */
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

/**
 * Invoke all subscribers with the new token and clear the queue.
 */
function onRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

// Request interceptor to attach access token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

// Response interceptor to handle connection errors (retry) and expired tokens
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle connection errors with automatic retry.
    // Only retry idempotent methods — retrying POST /reports after a timeout
    // created duplicate days and re-sent large photo payloads.
    if (isConnectionError(error) && originalRequest) {
      const method = (originalRequest.method || "get").toLowerCase();
      const isIdempotent =
        method === "get" || method === "head" || method === "options";
      if (!isIdempotent) {
        return Promise.reject(error);
      }

      const retryCount = originalRequest._retryCount || 0;

      // Only retry if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        originalRequest._retryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);

        // Wait before retrying (exponential backoff)
        await sleep(delay);

        // Retry the request
        return axiosInstance(originalRequest);
      }

      // Max retries exceeded, reject with the original error
      console.error(
        `Connection error after ${MAX_RETRIES} retries:`,
        error.message || error.code
      );
      return Promise.reject(error);
    }

    // Only handle 401s and only retry once per request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If the failed request is the refresh endpoint itself, force logout
      if (
        originalRequest.url &&
        originalRequest.url.includes("/auth/refresh-token")
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        sessionStorage.setItem("sessionExpired", "true");
        window.location.href = "/login";
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem("refreshToken");

      // No refresh token available -> force logout
      if (!refreshToken) {
        localStorage.removeItem("token");
        sessionStorage.setItem("sessionExpired", "true");
        window.location.href = "/login";
        return Promise.reject(error);
      }

      // If a refresh is already in progress, queue this request and resolve when done
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      // Start refresh flow
      isRefreshing = true;
      try {
        // Use plain axios (not axiosInstance) to avoid this interceptor
        const res = await axios.post("/api/auth/refresh-token", {
          refreshToken,
        });

        const newAccessToken = res.data.accessToken;
        const newRefreshToken = res.data.refreshToken;
        localStorage.setItem("token", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // Notify any listeners (e.g., UserContext) that token updated
        window.dispatchEvent(new Event("tokenUpdated"));

        // Resolve queued requests with the new token
        onRefreshed(newAccessToken);

        // Retry original request with new token
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Failed to refresh token:", refreshError);

        // If refresh fails, log out the user
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        sessionStorage.setItem("sessionExpired", "true"); // Used on the login page to display a message
        window.location.href = "/login"; // Redirect user to the login page

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
